import { useMemo, useState } from 'react';
import { RichTextEditor, RichTextView } from '../RichText';
import { useToast } from '../Toast';
import {
  sendBulkEmail,
  type EmailTemplate,
  type BulkEmailResult,
} from '../../lib/members-admin-queries';

/**
 * Compose modal for the admin "Email selected" action. Two locked branded
 * templates, a rich-text body, an optional invite/sign-in button, a live
 * preview, and a "send test to myself" before the real blast. The heavy
 * lifting (per-recipient links, suppression, branding, logging) is all
 * server-side - this is just the composer.
 */

const TEMPLATE_META: Record<EmailTemplate, { label: string; eyebrow: string; blurb: string }> = {
  invitation: {
    label: 'Invitation',
    eyebrow: 'A founding-member invitation',
    blurb: 'Best when the button carries a sign-up link for new members.',
  },
  announcement: {
    label: 'Announcement',
    eyebrow: 'A note from Converted Closet',
    blurb: 'A general update or note to existing members.',
  },
};

interface Recipient {
  email: string;
  display_name: string | null;
}

interface Props {
  recipients: Recipient[];
  onClose: () => void;
  onSent?: (result: BulkEmailResult) => void;
}

export function BulkEmailModal({ recipients: recipientsProp, onClose, onSent }: Props) {
  const toast = useToast();
  // Snapshot the recipient list on open so the parent clearing its selection
  // after a successful send doesn't empty this dialog mid-flight.
  const [recipients] = useState<Recipient[]>(recipientsProp);
  const [template, setTemplate] = useState<EmailTemplate>('announcement');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [includeButton, setIncludeButton] = useState(false);
  const [busy, setBusy] = useState<'idle' | 'test' | 'send'>('idle');
  const [result, setResult] = useState<BulkEmailResult | null>(null);

  const hasBody = useMemo(
    () => bodyHtml.replace(/<[^>]+>/g, '').trim().length > 0,
    [bodyHtml],
  );
  const canSend = subject.trim().length > 0 && hasBody && recipients.length > 0;

  async function doSend(test: boolean) {
    if (!test && !canSend) return;
    if (!test) {
      const ok = window.confirm(
        `Send this email to ${recipients.length} member${recipients.length === 1 ? '' : 's'}?\n\n` +
        `Anyone unsubscribed or not on the member list is skipped automatically.` +
        (includeButton ? `\n\nEach recipient gets their own single-use sign-in link.` : ''),
      );
      if (!ok) return;
    }
    setBusy(test ? 'test' : 'send');
    try {
      const res = await sendBulkEmail({
        recipients: recipients.map((r) => r.email),
        template,
        subject: subject.trim(),
        bodyHtml,
        includeInviteButton: includeButton,
        test,
      });
      if (test) {
        toast.success(res.sent > 0 ? 'Test sent to your address.' : 'Test could not be sent.');
      } else {
        setResult(res);
        const parts = [`Sent to ${res.sent}`];
        if (res.skipped.length) parts.push(`${res.skipped.length} skipped`);
        if (res.failed.length) parts.push(`${res.failed.length} failed`);
        toast.success(parts.join(' · '));
        onSent?.(res);
      }
    } catch (err: any) {
      const msg = err?.message ?? 'Send failed.';
      toast.error(
        /email_not_configured/.test(msg)
          ? 'Email not set up yet: RESEND_API_KEY / INVITE_FROM_EMAIL must be configured on Cloudflare Pages.'
          : msg,
      );
    } finally {
      setBusy('idle');
    }
  }

  const meta = TEMPLATE_META[template];

  return (
    <div
      className="admin__modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Email selected members"
      onClick={(e) => { if (e.target === e.currentTarget && busy === 'idle') onClose(); }}
    >
      <div className="admin__modal" style={{ maxWidth: 760, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
          <h3 style={{ marginTop: 0, marginBottom: 4 }}>
            Email {recipients.length} member{recipients.length === 1 ? '' : 's'}
          </h3>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose} disabled={busy !== 'idle'}>
            Close
          </button>
        </div>

        {result ? (
          <SendResult result={result} onClose={onClose} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 'var(--space-5)', marginTop: 'var(--space-3)' }}>
            {/* ---- Compose column ---- */}
            <div style={{ minWidth: 0 }}>
              <label style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
                <span className="onb__label">Template</span>
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  {(['announcement', 'invitation'] as EmailTemplate[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`btn btn--sm ${template === t ? 'btn--secondary' : 'btn--ghost'}`}
                      onClick={() => setTemplate(t)}
                    >
                      {template === t ? '✓ ' : ''}{TEMPLATE_META[t].label}
                    </button>
                  ))}
                </div>
                <small style={{ display: 'block', marginTop: 6, color: 'var(--text-soft)' }}>{meta.blurb}</small>
              </label>

              <label style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
                <span className="onb__label">Subject</span>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Subject line"
                  style={{ width: '100%' }}
                  disabled={busy !== 'idle'}
                />
              </label>

              <div style={{ marginBottom: 'var(--space-3)' }}>
                <span className="onb__label">Message</span>
                <small style={{ display: 'block', margin: '4px 0 6px', color: 'var(--text-soft)' }}>
                  Use <code>{'{{first_name}}'}</code> to personalize the greeting.
                </small>
                <RichTextEditor
                  value=""
                  onChange={setBodyHtml}
                  disabled={busy !== 'idle'}
                  placeholder="Write your message…"
                />
              </div>

              <label className="onb__check" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={includeButton}
                  onChange={(e) => setIncludeButton(e.target.checked)}
                  disabled={busy !== 'idle'}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <span className="onb__label" style={{ margin: 0 }}>Include the invite / sign-in button</span>
                  <small style={{ display: 'block', color: 'var(--text-soft)' }}>
                    Adds a branded button below your message. Each recipient gets their own single-use link:
                    a sign-up link if they haven’t joined, or a one-time sign-in link if they have.
                  </small>
                </span>
              </label>
            </div>

            {/* ---- Preview column ---- */}
            <div style={{ minWidth: 0 }}>
              <span className="onb__label">Preview</span>
              <div style={{
                marginTop: 6,
                background: '#FAFAF7',
                border: '1px solid #E8E5DE',
                padding: 20,
                borderRadius: 4,
                maxHeight: 360,
                overflow: 'auto',
              }}>
                <div style={{ background: '#fff', border: '1px solid #E8E5DE', padding: 24 }}>
                  <div style={{ fontFamily: 'Helvetica,Arial,sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#6F6A63', fontWeight: 600 }}>
                    Converted Closet
                  </div>
                  <div style={{ marginTop: 4, fontFamily: 'Helvetica,Arial,sans-serif', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#9A938A' }}>
                    {meta.eyebrow}
                  </div>
                  <div style={{ marginTop: 16, fontFamily: "Georgia,'Times New Roman',serif", color: '#2B2926' }}>
                    {hasBody
                      ? <RichTextView html={bodyHtml} />
                      : <p style={{ color: '#9A938A', fontStyle: 'italic' }}>Your message preview appears here.</p>}
                  </div>
                  {includeButton && (
                    <div style={{ marginTop: 20 }}>
                      <span style={{ display: 'inline-block', padding: '12px 24px', background: '#14110F', color: '#FAFAF7', fontFamily: 'Helvetica,Arial,sans-serif', fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600 }}>
                        {template === 'invitation' ? 'Accept the invitation →' : 'Open Converted Closet →'}
                      </span>
                    </div>
                  )}
                  <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #E8E5DE', fontFamily: "Georgia,'Times New Roman',serif", fontSize: 13, color: '#6F6A63' }}>
                    You’re receiving this because you’re a member of Converted Closet. · <u>Unsubscribe</u>
                  </div>
                </div>
              </div>

              <details style={{ marginTop: 'var(--space-3)' }}>
                <summary style={{ cursor: 'pointer', color: 'var(--text-soft)', fontSize: '0.9em' }}>
                  Recipients ({recipients.length})
                </summary>
                <div style={{ maxHeight: 120, overflow: 'auto', marginTop: 6, fontSize: '0.85em', color: 'var(--text-soft)' }}>
                  {recipients.map((r) => <div key={r.email}><code>{r.email}</code></div>)}
                </div>
              </details>
            </div>
          </div>
        )}

        {!result && (
          <div className="admin__row-actions" style={{ marginTop: 'var(--space-5)', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => doSend(true)}
              disabled={busy !== 'idle' || !subject.trim() || !hasBody}
              title="Send a copy to your own address"
            >
              {busy === 'test' ? 'Sending…' : 'Send test to myself'}
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => doSend(false)}
              disabled={busy !== 'idle' || !canSend}
            >
              {busy === 'send' ? 'Sending…' : `Send to ${recipients.length}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SendResult({ result, onClose }: { result: BulkEmailResult; onClose: () => void }) {
  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      <p style={{ fontSize: '1.05em' }}>
        <strong>{result.sent}</strong> email{result.sent === 1 ? '' : 's'} sent.
      </p>
      {result.skipped.length > 0 && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <span className="onb__label">Skipped ({result.skipped.length})</span>
          <div style={{ maxHeight: 140, overflow: 'auto', marginTop: 6, fontSize: '0.85em', color: 'var(--text-soft)' }}>
            {result.skipped.map((s) => (
              <div key={s.email}><code>{s.email}</code> - {labelReason(s.reason)}</div>
            ))}
          </div>
        </div>
      )}
      {result.failed.length > 0 && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <span className="onb__label" style={{ color: 'var(--danger)' }}>Failed ({result.failed.length})</span>
          <div style={{ maxHeight: 140, overflow: 'auto', marginTop: 6, fontSize: '0.85em', color: 'var(--text-soft)' }}>
            {result.failed.map((f) => (
              <div key={f.email}><code>{f.email}</code> - {f.error}</div>
            ))}
          </div>
        </div>
      )}
      <div className="admin__row-actions" style={{ marginTop: 'var(--space-5)', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn--primary btn--sm" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}

function labelReason(reason: string): string {
  switch (reason) {
    case 'suppressed': return 'unsubscribed / bounced';
    case 'not_whitelisted': return 'not on the member list';
    default: return reason;
  }
}
